import { Navigate, Outlet, RouteObject } from "react-router-dom";
import {
  ContractPricingProfilesRoute,
  CustomRatesRoute,
  PricingProfileRoute,
  StateRatesRoute,
  ZoneRatesRoute,
} from "@/components/organisms";
import { Config, Restricted } from "@/components/pages";
import { useOperatorAccess } from "@/hooks";
import { RequirePermission, usePermissions } from "@/permissions";
import { EPermission } from "@/permissions/types";

/**
 * User Accounts stays reachable for solo / unsubscribed admins even if they lack the staff-management
 * permissions, so they can still view their owner (and Driver App) login details.
 */
const RequireUserAccountsAccess = () => {
  const { hasAllPermissions } = usePermissions();
  const { isUnsubscribed, isSolo, isAdmin } = useOperatorAccess();
  const hasAccess =
    hasAllPermissions([EPermission.ProviderAccountDirectoryManage, EPermission.ProviderConfigUserAccountsManage]) ||
    ((isUnsubscribed || isSolo) && isAdmin);

  if (!hasAccess) return <Restricted className="-mb-12" />;
  return <Outlet />;
};

/** Unsubscribed operators only have the User Accounts tab, so land them there instead of Business. */
const ConfigIndexRedirect = () => {
  const { isUnsubscribed } = useOperatorAccess();
  return <Navigate to={isUnsubscribed ? "user-accounts" : "business"} replace />;
};

/**
 * Settings stays reachable for solo / unsubscribed admins even though unsubscribing can strip
 * providerConfig:view — they still need User Accounts to view their login details.
 */
const RequireConfigAccess = () => {
  const { hasPermission } = usePermissions();
  const { isUnsubscribed, isSolo, isAdmin } = useOperatorAccess();
  const hasAccess = hasPermission(EPermission.ProviderConfigView) || ((isUnsubscribed || isSolo) && isAdmin);

  if (!hasAccess) return <Restricted className="-mb-12" />;
  return <Outlet />;
};

const businessRoutes: RouteObject = {
  path: "business",
  handle: { pageName: "Business Settings" },
  children: [
    {
      index: true,
      element: <Config.Business />,
    },
    {
      path: ":pageSlug?",
      element: <RequirePermission permission={EPermission.ProviderConfigProfileAdvancedManage} />,
      children: [
        {
          path: "access-control",
          handle: { pageName: "Access Control" },
          element: <RequirePermission permission={EPermission.ProviderConfigAccessControlManage} />,
          children: [
            {
              index: true,
              element: <Config.Business.AccessControl />,
            },
            {
              path: "edit",
              element: <Config.Business.AccessControl.Edit />,
            },
          ],
        },
      ],
    },
    {
      path: "business-details",
      handle: { pageName: "Business Details" },
      element: <RequirePermission permission={EPermission.ProviderConfigBusinessDetailsManage} />,
      children: [
        {
          index: true,
          element: <Config.Business.BusinessDetails />,
        },
        {
          path: "edit",
          element: <Config.Business.BusinessDetails.Edit />,
        },
      ],
    },
    {
      path: "website-details",
      handle: { pageName: "Website Details" },
      element: <RequirePermission permission={EPermission.ProviderConfigWebsiteDetailsManage} />,
      children: [
        {
          index: true,
          element: <Config.Business.WebsiteDetails />,
        },
        {
          path: "edit",
          element: <Config.Business.WebsiteDetails.Edit />,
        },
      ],
    },
    {
      path: "email-notifications",
      handle: { pageName: "Email Notifications" },
      element: <RequirePermission permission={EPermission.ProviderConfigEmailNotificationsManage} />,
      children: [
        {
          index: true,
          element: <Config.Business.EmailNotifications />,
        },
        {
          path: "edit",
          element: <Config.Business.EmailNotifications.Edit />,
        },
      ],
    },
    {
      path: "sms-voice-notifications",
      handle: { pageName: "SMS / Voice Notifications" },
      element: <RequirePermission permission={EPermission.ProviderConfigSmsVoiceNotificationsManage} />,
      children: [
        {
          index: true,
          element: <Config.Business.SMSVoiceNotifications />,
        },
        {
          path: "edit",
          element: <Config.Business.SMSVoiceNotifications.Edit />,
        },
      ],
    },
    {
      path: "display-options",
      handle: { pageName: "Display Options" },
      element: <RequirePermission permission={EPermission.ProviderConfigDisplayOptionsManage} />,
      children: [
        {
          index: true,
          element: <Config.Business.DisplayOptions />,
        },
        {
          path: "edit",
          element: <RequirePermission permission={EPermission.ProviderConfigDisplayOptionsAdvancedManage} />,
          children: [
            {
              index: true,
              element: <Config.Business.DisplayOptions.Edit />,
            },
          ],
        },
      ],
    },
  ],
};

const tripRoutes: RouteObject = {
  path: "trip",
  handle: { pageName: "Trip Settings" },
  element: <RequirePermission permission={EPermission.ProviderConfigTripSettingsManage} />,
  children: [
    {
      index: true,
      element: <Config.Trip />,
    },
    {
      path: ":pageSlug?",
      element: <RequirePermission permission={EPermission.ProviderConfigPricingManage} />,
      children: [
        {
          path: "trip-cost",
          handle: { pageName: "Trip Cost" },
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.TripCostV2 />,
            },
            {
              path: "default-rates",
              handle: { pageName: "Default Rates" },
              element: <Config.Trip.TripPanel.TripCostV2.DefaultRates />,
            },
            {
              path: "custom-rates",
              handle: { pageName: "Custom Rates" },
              element: (
                <RequirePermission permission={EPermission.CustomRatesManage}>
                  <CustomRatesRoute />
                </RequirePermission>
              ),
              children: [
                {
                  index: true,
                  element: <Config.Trip.TripPanel.TripCostV2.CustomRates />,
                },
                {
                  path: ":slug",
                  handle: { pageKey: "stateName" },
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.TripCostV2.CustomRates.StateRates />,
                    },
                    {
                      path: "edit",
                      element: <Config.Trip.TripPanel.TripCostV2.CustomRates.StateRatesEdit />,
                    },
                  ],
                },
              ],
            },
            {
              path: "contract-pricing-profiles",
              handle: { pageName: "Contract Pricing Profiles" },
              element: (
                <RequirePermission permission={EPermission.ContractPricingProfileManage}>
                  <ContractPricingProfilesRoute />
                </RequirePermission>
              ),
              children: [
                {
                  index: true,
                  element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles />,
                },
                {
                  path: "add",
                  element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.Add />,
                },
                {
                  path: ":id",
                  handle: {
                    pageKey: "profileName",
                    pageName: "Pricing Profile",
                  },
                  element: <PricingProfileRoute />,
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile />,
                    },
                    {
                      path: "state-rates",
                      handle: { pageName: "State Rates" },
                      element: <StateRatesRoute />,
                      children: [
                        {
                          index: true,
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.StateRates />,
                        },
                        {
                          path: ":slug",
                          handle: { pageKey: "stateName" },
                          children: [
                            {
                              index: true,
                              element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.StateRates.View />,
                            },
                            {
                              path: "edit",
                              element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.StateRates.Edit />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      path: "zone-rates",
                      handle: { pageName: "Zone Rates" },
                      element: <ZoneRatesRoute />,
                      children: [
                        {
                          index: true,
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.ZoneRates />,
                        },
                        {
                          path: "add",
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.ZoneRates.Add />,
                        },
                        {
                          path: ":slug",
                          handle: { pageKey: "zoneName" },
                          element: <Config.Trip.TripPanel.TripCostV2.ContractPricingProfiles.PricingProfile.ZoneRates.Edit />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: "time-and-day",
          handle: { pageName: "Time & Day (Multiplier)" },
          element: <RequirePermission permission={EPermission.ProviderConfigTimeAndDayManage} />,
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.TimeAndDay />,
            },
            {
              path: "edit",
              children: [{ index: true, element: <Config.Trip.TripPanel.TimeAndDay.Edit /> }],
            },
          ],
        },

        {
          path: "cancellation-fees",
          handle: { pageName: "Cancellation Fees" },
          element: <RequirePermission permission={EPermission.ProviderConfigCancellationFeesManage} />,
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.CancellationFees />,
            },
            {
              path: ":mode?/:type?",
              handle: { pageName: "Cancellation Fees" },
              element: <Config.Trip.TripPanel.CancellationFees />,
            },
          ],
        },
        {
          path: "unscheduled-stops",
          handle: { pageName: "Unscheduled Stops" },
          element: <RequirePermission permission={EPermission.ProviderConfigUnscheduledStopsManage} />,
          children: [
            {
              index: true,
              element: <Config.Trip.TripPanel.UnscheduledStops />,
            },
            {
              path: "edit",
              element: <Config.Trip.TripPanel.UnscheduledStops.Edit />,
            },
          ],
        },
        {
          path: "hourly-daily-hire",
          handle: { pageName: "Hourly - Daily Hire" },
          element: <RequirePermission permission={EPermission.ProviderConfigHourlyDailyHireManage} />,
          children: [
            {
              path: "*",
              element: <Config.Trip.TripPanel.HourlyDailyHire />,
              children: [
                {
                  index: true,
                  element: <Navigate to="hourly" replace />,
                },
                {
                  path: "hourly",
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Hourly />,
                    },
                    {
                      path: ":mode?/:type?",
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Hourly />,
                    },
                  ],
                },
                {
                  path: "daily",
                  children: [
                    {
                      index: true,
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Daily />,
                    },
                    {
                      path: ":mode?/:type?",
                      element: <Config.Trip.TripPanel.HourlyDailyHire.Daily />,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "vehicle-types",
      handle: { pageName: "Transfer & Vehicle Options" },
      element: <RequirePermission permission={EPermission.ProviderConfigVehicleManage} />,
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.VehicleTypes />,
        },
        {
          path: ":vehicleTypeId",
          handle: { pageName: "Edit Vehicle Type" },
          element: <Config.Trip.TripPanel.VehicleTypes.Edit />,
        },
        {
          path: "add",
          handle: { pageName: "Add Vehicle Type" },
          element: <Config.Trip.TripPanel.VehicleTypes.Add />,
        },
      ],
    },
    {
      path: "vehicle-categories",
      element: <Navigate to="../vehicle-types" replace />,
    },
    {
      path: "vehicle-categories/*",
      element: <Navigate to="../vehicle-types" replace />,
    },
    {
      path: "transfer-types",
      handle: { pageName: "Transfer & Vehicle Options" },
      element: <RequirePermission permission={EPermission.ProviderConfigVehicleManage} />,
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.TransferTypes />,
        },
        {
          path: ":transferTypeId",
          handle: { pageName: "Edit Transfer Type" },
          element: <Config.Trip.TripPanel.TransferTypes.Edit />,
        },
        {
          path: "add",
          handle: { pageName: "Add Transfer Type" },
          element: <Config.Trip.TripPanel.TransferTypes.Add />,
        },
      ],
    },
    {
      path: "fixed-cost-extras",
      handle: { pageName: "Transfer & Vehicle Options" },
      element: <Config.Trip.TripPanel.FixedCostExtras />,
      children: [
        {
          path: "add",
          element: <Config.Trip.TripPanel.FixedCostExtras.Add />,
        },
        {
          path: ":fixedCostExtraId",
          element: <Config.Trip.TripPanel.FixedCostExtras.Edit />,
        },
      ],
    },
    {
      path: "billing-settings",
      handle: { pageName: "Billing Settings" },
      element: <RequirePermission permission={EPermission.ProviderConfigProfileAdvancedManage} />,
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.BillingSettings />,
        },
        {
          path: "edit",
          element: <Config.Trip.TripPanel.BillingSettings.Edit />,
        },
      ],
    },
    {
      path: "waiting-times",
      handle: { pageName: "Waiting Times" },
      element: (
        <RequirePermission allOf={[EPermission.ProviderConfigProfileAdvancedManage, EPermission.ProviderConfigWaitingTimesManage]} />
      ),
      children: [
        {
          index: true,
          element: <Config.Trip.TripPanel.WaitingTimes />,
        },
        {
          path: "edit",
          element: <Config.Trip.TripPanel.WaitingTimes.Edit />,
        },
      ],
    },
    {
      path: "dispatch-settings",
      handle: { pageName: "Dispatch Settings" },
      element: <RequirePermission permission={EPermission.ProviderConfigProfileAdvancedManage} />,
      children: [
        {
          index: true,
          element: <Config.Trip.DispatchPanel.DispatchSettings />,
        },
        {
          path: ":mode?/:type?",
          element: <Config.Trip.DispatchPanel.DispatchSettings />,
        },
      ],
    },
  ],
};

const documentRoutes: RouteObject = {
  path: "documents",
  handle: { pageName: "Documents Links" },
  children: [
    {
      index: true,
      element: <Config.Documents />,
    },
    {
      path: "terms-and-conditions",
      handle: { pageName: "RideMinder Platform Usage Subscription Agreement" },
      element: <Config.Documents.TermsAndConditions />,
    },
  ],
};

const marketingRoutes: RouteObject = {
  path: "marketing",
  handle: { pageName: "Marketing Tools" },
  element: <RequirePermission permission={EPermission.ProviderConfigMarketingToolManage} />,
  children: [
    {
      index: true,
      element: <Config.Marketing />,
    },
    {
      path: "qr-templates",
      handle: { pageName: "Contact Printer Supplier" },
      children: [
        {
          index: true,
          element: <Config.Marketing.ClientTools.SendToPrinter />,
        },
      ],
    },
  ],
};

const userAccountsRoutes: RouteObject = {
  path: "user-accounts",
  element: <RequireUserAccountsAccess />,
  handle: { pageName: "User Accounts" },
  children: [
    {
      index: true,
      element: <Config.UserAccounts />,
    },
    {
      path: ":id/history",
      handle: { pageName: "History Logs" },
      element: <Config.UserAccounts.History />,
    },
  ],
};

export const configRoutes: RouteObject = {
  path: "config",
  element: <RequireConfigAccess />,
  handle: { pageName: "Settings" },
  children: [
    {
      path: "*",
      element: <Config />,
      children: [
        {
          index: true,
          element: <ConfigIndexRedirect />,
        },
        businessRoutes,
        tripRoutes,
        marketingRoutes,
        documentRoutes,
        userAccountsRoutes,
      ],
    },
  ],
};
